import { Box, Button, Paper, TextInput } from '@mantine/core';
import { useTimeout } from '@mantine/hooks';
import { motion } from 'framer-motion';
import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useHostContext } from '@/srcV2/context/HostContext.tsx';
import useAppStore from '@/srcV2/store/app.ts';
import { normalizeValue } from '@/srcV2/utils';
import {
	buildPlaceholderHints,
	PLACEHOLDER_ANIMATION_DELAY_MS
} from '../../utils';
import { runPlaceholderCycle } from './utils';

type Props = {
	hasInteracted: boolean;
	onInteraction: () => void;
};

const CreateYourAiControls: FC<Props> = ({ hasInteracted, onInteraction }) => {
	const { localization } = useHostContext();
	const { businessSource, setBusinessSource } = useAppStore(
		useShallow((state) => ({
			businessSource: state.businessSource,
			setBusinessSource: state.setBusinessSource
		}))
	);
	const value = businessSource ?? '';
	const normalizedValue = normalizeValue(value) ?? '';
	const animatedPlaceholderRef = useRef<HTMLSpanElement | null>(null);
	const placeholderAnimationTokenRef = useRef(0);
	const typingDelayResolveRef = useRef<(() => void) | null>(null);
	const pauseDelayResolveRef = useRef<(() => void) | null>(null);
	const deleteDelayResolveRef = useRef<(() => void) | null>(null);

	const writeAnimatedPlaceholder = useCallback((nextPlaceholder: string) => {
		if (animatedPlaceholderRef.current) {
			animatedPlaceholderRef.current.textContent = nextPlaceholder;
		}
	}, []);

	const typingDelayTimeout = useTimeout(() => {
		typingDelayResolveRef.current?.();
		typingDelayResolveRef.current = null;
	}, PLACEHOLDER_ANIMATION_DELAY_MS.type);
	const pauseDelayTimeout = useTimeout(() => {
		pauseDelayResolveRef.current?.();
		pauseDelayResolveRef.current = null;
	}, PLACEHOLDER_ANIMATION_DELAY_MS.pause);
	const deleteDelayTimeout = useTimeout(() => {
		deleteDelayResolveRef.current?.();
		deleteDelayResolveRef.current = null;
	}, PLACEHOLDER_ANIMATION_DELAY_MS.delete);

	const waitForPlaceholderDelay = useCallback(() => {
		return {
			delete: () =>
				new Promise<void>((resolve) => {
					deleteDelayResolveRef.current = resolve;
					deleteDelayTimeout.start();
				}),
			pause: () =>
				new Promise<void>((resolve) => {
					pauseDelayResolveRef.current = resolve;
					pauseDelayTimeout.start();
				}),
			type: () =>
				new Promise<void>((resolve) => {
					typingDelayResolveRef.current = resolve;
					typingDelayTimeout.start();
				})
		};
	}, [deleteDelayTimeout, pauseDelayTimeout, typingDelayTimeout]);

	const stopPlaceholderAnimation = useCallback(() => {
		placeholderAnimationTokenRef.current += 1;
		typingDelayTimeout.clear();
		pauseDelayTimeout.clear();
		deleteDelayTimeout.clear();
		typingDelayResolveRef.current = null;
		pauseDelayResolveRef.current = null;
		deleteDelayResolveRef.current = null;
		writeAnimatedPlaceholder('');
	}, [
		deleteDelayTimeout,
		pauseDelayTimeout,
		typingDelayTimeout,
		writeAnimatedPlaceholder
	]);

	useEffect(() => {
		if (!normalizedValue || hasInteracted) {
			return;
		}

		onInteraction();
	}, [hasInteracted, normalizedValue, onInteraction]);

	const placeholderHints = useMemo(
		() =>
			buildPlaceholderHints(
				localization.translate(
					'vibeCreator',
					'createNewAIReceptionist.defaultWebsite'
				),
				localization.translate(
					'vibeCreator',
					'createNewAIReceptionist.businessDescriptionHint'
				)
			),
		[localization]
	);

	const hasInputInteracted = hasInteracted || Boolean(normalizedValue);
	const showAnimatedPlaceholder =
		!hasInputInteracted && (value?.trim().length ?? 0) === 0;
	const isSubmitDisabled = (value?.trim().length ?? 0) === 0;

	const handleAnimatedPlaceholderRef = useCallback(
		(node: HTMLSpanElement | null) => {
			animatedPlaceholderRef.current = node;
			stopPlaceholderAnimation();

			if (!node || hasInputInteracted) {
				return;
			}

			const token = placeholderAnimationTokenRef.current;
			const wait = waitForPlaceholderDelay();

			void runPlaceholderCycle({
				getCurrentToken: () => placeholderAnimationTokenRef.current,
				placeholderHints,
				token,
				wait,
				writeAnimatedPlaceholder
			});
		},
		[
			hasInputInteracted,
			placeholderHints,
			stopPlaceholderAnimation,
			waitForPlaceholderDelay,
			writeAnimatedPlaceholder
		]
	);

	const handleInputInteraction = useCallback(() => {
		if (!hasInputInteracted) {
			onInteraction();
		}

		stopPlaceholderAnimation();
	}, [hasInputInteracted, onInteraction, stopPlaceholderAnimation]);

	const handleWebsiteChange = useCallback(
		(nextValue: string) => {
			setBusinessSource(nextValue || null);

			if (!hasInputInteracted) {
				handleInputInteraction();
			}
		},
		[handleInputInteraction, hasInputInteracted, setBusinessSource]
	);

	const handleSubmit = useCallback(() => {
		const normalizedWebsite = value?.trim() ?? '';

		if (!normalizedWebsite) {
			return;
		}

		setBusinessSource(normalizedWebsite);
	}, [setBusinessSource, value]);

	return (
		<Paper
			classNames={{
				root: 'border h-auto sm:h-[72px] border-gray-200 bg-white flex flex-col gap-3 sm:flex-row sm:items-center'
			}}
			data-testid="create-new-ai-receptionist-controls-card"
			p={8}
			radius={18}
			shadow="xs"
		>
			<Box
				className="relative flex-1"
				data-testid="create-your-ai-controls-input-root"
			>
				<TextInput
					classNames={{
						input: 'bg-transparent px-3 text-lg font-medium text-inherit',
						root: 'text-inherit'
					}}
					data-testid="create-your-ai-controls-input"
					onChange={(event) => handleWebsiteChange(event.currentTarget.value)}
					onFocus={handleInputInteraction}
					placeholder=""
					radius="xl"
					size="lg"
					value={value ?? ''}
					variant="unstyled"
				/>

				{showAnimatedPlaceholder && (
					<div
						className="pointer-events-none absolute inset-y-0 left-3 right-3 flex items-center overflow-hidden"
						data-testid="create-your-ai-controls-placeholder"
					>
						<span
							className="truncate text-lg font-medium"
							data-testid="create-your-ai-controls-placeholder-text"
							ref={handleAnimatedPlaceholderRef}
						/>
						<motion.span
							animate={{ opacity: [1, 0, 1] }}
							className="ml-0.5 text-lg font-medium"
							transition={{
								duration: 1,
								ease: 'easeInOut',
								repeat: Number.POSITIVE_INFINITY
							}}
						>
							|
						</motion.span>
					</div>
				)}
			</Box>

			<Button
				classNames={{
					label: 'text-inherit font-normal',
					root: 'shrink-0'
				}}
				data-testid="create-your-ai-controls-submit"
				disabled={isSubmitDisabled}
				onClick={handleSubmit}
				radius="xl"
				size="lg"
				vars={() => ({
					root: {
						'--button-bd': 'transparent'
					}
				})}
			>
				{localization.translate(
					'vibeCreator',
					'createNewAIReceptionist.ctaLabel'
				)}
			</Button>
		</Paper>
	);
};

export default CreateYourAiControls;
